/**
 * Donut / camembert : répartition d'une métrique par dimension catégorielle.
 *
 * Porté de Metrix (`features/reports/widgets/DonutPieWidget.tsx`), branché sur le
 * socle Orion : `chartStyle` (palette, tooltip, légende), `fieldCatalog` (libellés
 * FR + formats) et `useDrillDown` (cliquer une part descend d'un niveau).
 *
 * `buildDonutOption` est une fonction pure, testée sans DOM.
 */
import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { WidgetConfig } from '../schemas';
import type { WidgetQueryResult } from '../useWidgetData';
import { useFieldCatalog, metricLabel, metricFormat, type FieldCatalog } from '../fieldCatalog';
import { CHART_COLORS, buildLegendStyle, type LegendPosition } from '../chartStyle';
import { useChartPalette } from '../ChartPaletteContext';
import { resolveWidgetPalette } from '../chartPalette';
import { useDrillDown, type DrillItem } from '../useDrillDown';
import { DrillBreadcrumb } from '../DrillBreadcrumb';
import { aggregateCategoryItems, buildItemTooltip, type TooltipItemParam } from './categoryItems';

// Cadre + padding fournis par WidgetShell (WidgetRenderer) : ici, hauteur + flex.
const SHELL = 'h-full flex flex-col';

interface PieDataItem {
  name: string;
  value: number;
  itemStyle?: { color: string };
}

interface PieSeries {
  name: string;
  type: 'pie';
  radius: string | [string, string];
  center: [string, string];
  data: PieDataItem[];
  cursor: string;
  label: Record<string, unknown>;
  labelLine: Record<string, unknown>;
  emphasis: Record<string, unknown>;
}

export interface DonutOption {
  title?: { text: string; left: string; top: number; textStyle: Record<string, unknown> };
  tooltip: ReturnType<typeof buildItemTooltip>;
  legend?: ReturnType<typeof buildLegendStyle>;
  color: string[];
  series: PieSeries[];
}

const DEFAULT_LIMIT = 10;

/** Options ECharts du donut / camembert (fonction pure — c'est elle qu'on teste). */
export function buildDonutOption(
  config: WidgetConfig,
  data: WidgetQueryResult | null,
  catalog: FieldCatalog | null,
  drillItems?: DrillItem[] | null,
  palette: string[] = CHART_COLORS,
): DonutOption {
  const metric = config.metrics?.[0];
  const dimension = config.dimensions?.[0];
  const display = (config.display ?? {}) as Record<string, unknown>;

  const limit = (display.limit as number | undefined) ?? DEFAULT_LIMIT;
  const showLegend = (display.show_legend as boolean | undefined) ?? true;
  const showLabels = (display.show_labels as boolean | undefined) ?? false;
  const legendPosition = (display.legend_position as LegendPosition | undefined) ?? 'bottom';
  const isPie = display.variant === 'pie';
  const hasTitle = !!config.title;

  const format = metricFormat(catalog, metric ?? '');
  const label = metric ? metricLabel(catalog, metric, config.metric_labels) : '';

  const items: DrillItem[] =
    drillItems ??
    (metric && dimension && data ? aggregateCategoryItems(data.current, dimension, metric, catalog, limit) : []);

  const seriesColors = (config.format?.series_colors as Record<string, string> | undefined) ?? {};

  const option: DonutOption = {
    title: hasTitle
      ? {
          text: config.title as string,
          left: 'center',
          top: 4,
          textStyle: { fontSize: 13, fontWeight: 600, color: '#1B2559' },
        }
      : undefined,
    // Tooltip d'item (le libellé vient de `params.name`, pas d'un axe) : style `chartStyle`, valeur formatée.
    tooltip: buildItemTooltip(format, true),
    legend: showLegend
      ? buildLegendStyle(
          items.map((i) => i.key),
          legendPosition,
          hasTitle,
        )
      : undefined,
    color: palette,
    series: [],
  };

  if (!items.length) return option;

  option.series = [
    {
      name: label,
      type: 'pie',
      radius: isPie ? '62%' : ['42%', '65%'],
      center: legendPosition === 'left' ? ['62%', '48%'] : legendPosition === 'right' ? ['38%', '48%'] : ['50%', '46%'],
      data: items.map((item) => ({
        name: item.key,
        value: item.value,
        ...(seriesColors[item.key] ? { itemStyle: { color: seriesColors[item.key] } } : {}),
      })),
      cursor: 'pointer',
      label: showLabels
        ? {
            show: true,
            fontSize: 11,
            color: '#1B2559',
            minShowLabelAngle: 12,
            formatter: (p: TooltipItemParam) =>
              `${p.name}\n${(p.percent ?? 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })} %`,
          }
        : { show: false },
      labelLine: showLabels ? { show: true, length: 8, length2: 6 } : { show: false },
      emphasis: {
        label: { show: true, fontSize: 12, fontWeight: 'bold' },
        itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.2)' },
      },
    },
  ];

  return option;
}

interface WidgetProps {
  config: WidgetConfig;
  data: WidgetQueryResult | null;
  loading?: boolean;
  error?: string;
}

export default function DonutPieWidget({ config, data, loading, error }: WidgetProps) {
  // Hooks avant tout early return (règle des hooks).
  const catalog = useFieldCatalog((data?.meta as { source_type?: string } | undefined)?.source_type);
  const palette = resolveWidgetPalette(config.format?.palette as string | string[] | undefined, useChartPalette());

  const metric = config.metrics?.[0];
  const dimension = config.dimensions?.[0];
  const display = (config.display ?? {}) as Record<string, unknown>;
  const drillDimensions = (display.drill_dimensions as string[] | undefined) ?? [];
  const period = (data?.meta as { period?: { start: string; end: string } } | undefined)?.period;

  const drill = useDrillDown({
    enabled: drillDimensions.length > 0 && !!config.data_source_id && !!metric && !!dimension && !!period,
    dimension: dimension ?? '',
    drillDimensions,
    dataSourceId: config.data_source_id ?? '',
    metric: metric ?? '',
    topN: (display.limit as number | undefined) ?? DEFAULT_LIMIT,
    period: period ?? { start: '', end: '' },
  });

  const option = useMemo(
    () => buildDonutOption(config, data, catalog, drill.drillItems, palette),
    [config, data, catalog, drill.drillItems, palette],
  );

  if (loading) {
    return (
      <div className={SHELL}>
        <div className="h-full w-full animate-pulse rounded bg-[#F4F5FA]" />
      </div>
    );
  }

  // Un échec de drill-down (400, timeout…) s'affiche dans l'état d'erreur du widget.
  const shownError = error ?? drill.error;

  if (shownError) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-red-600">{shownError}</span>
      </div>
    );
  }

  if (!data || !config.data_source_id || !metric || !dimension) {
    return (
      <div className={`${SHELL} items-center justify-center`}>
        <span className="text-sm text-[#8B8FA7]">À configurer</span>
      </div>
    );
  }

  return (
    <div className={SHELL}>
      <DrillBreadcrumb drillPath={drill.drillPath} catalog={catalog} onDrillTo={drill.drillTo} />
      {drill.isLoading ? (
        <div className="flex flex-1 items-center justify-center text-sm text-[#8B8FA7]">Chargement…</div>
      ) : option.series.length === 0 ? (
        <div className="flex flex-1 items-center justify-center text-sm italic text-[#8B8FA7]">Aucune donnée</div>
      ) : (
        <ReactECharts
          option={option}
          notMerge
          style={{ flex: 1, minHeight: 220 }}
          onEvents={
            drill.canDrillDown ? { click: (params: { name: string }) => void drill.handleDrillDown(params.name) } : {}
          }
        />
      )}
    </div>
  );
}
